import {
  createContext,
  Fragment,
  useContext,
  type FC,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import { type Lines } from "./popupContent";

/**
 * Fullscreen popups get a larger type scale from `xl` up, stepped down for
 * short laptop windows and up again for large desktops; every popup but
 * today's (variant A) balances its line wrapping. A keeps its classes exactly.
 */
export const PopupTypeContext = createContext<{
  fullscreen: boolean;
  balance: boolean;
}>({ fullscreen: false, balance: false });

const usePopupType = () => useContext(PopupTypeContext);

/** Joins copy lines with <br>; a "2xl" break keeps a space for narrower widths. */
export const LineBreaks: FC<{ value: Lines }> = ({ value }) => (
  <>
    {value.lines.map((line, index) => (
      <Fragment key={line}>
        {index > 0 &&
          (value.breakFrom === "2xl" ? (
            <>
              {" "}
              <br className="hidden 2xl:block" />
            </>
          ) : (
            <br />
          ))}
        {line}
      </Fragment>
    ))}
  </>
);

export const Eyebrow: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const { fullscreen } = usePopupType();
  return (
    <span
      className={cn(
        "font-trimMono text-[12px] font-medium uppercase leading-[16px] -tracking-[0.36px] text-[#1f2022] md:text-[13px] md:leading-[18px] md:-tracking-[0.39px] 2xl:text-[14px] 2xl:leading-[20px] 2xl:-tracking-[0.42px]",
        fullscreen &&
          "xl:text-[14px] xl:leading-[20px] 2xl:text-[16px] 2xl:leading-[22px]",
        className,
      )}
    >
      {children}
    </span>
  );
};

export const Headline: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const { fullscreen, balance } = usePopupType();
  return (
    <h2
      data-sweepstakes-step-heading
      tabIndex={-1}
      className={cn(
        "font-ppMori text-[28px] font-semibold leading-[32px] -tracking-[1.12px] text-[#1f2022] outline-none md:text-[32px] md:leading-[36px] md:-tracking-[1.28px] xl:text-[36px] xl:leading-[40px] xl:-tracking-[1.44px] 2xl:text-[48px] 2xl:leading-[52px] 2xl:-tracking-[1.92px]",
        fullscreen &&
          "xl:text-[52px] xl:leading-[56px] xl:-tracking-[2.08px] 2xl:text-[64px] 2xl:leading-[68px] 2xl:-tracking-[2.56px] [@media(min-width:1280px)_and_(max-height:959px)]:text-[44px] [@media(min-width:1280px)_and_(max-height:959px)]:leading-[48px] [@media(min-width:1280px)_and_(max-height:959px)]:-tracking-[1.76px] [@media(min-width:1920px)_and_(min-height:1080px)]:text-[76px] [@media(min-width:1920px)_and_(min-height:1080px)]:leading-[80px] [@media(min-width:1920px)_and_(min-height:1080px)]:-tracking-[3.04px]",
        balance && "text-balance",
        className,
      )}
    >
      {children}
    </h2>
  );
};

export const Subhead: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const { fullscreen, balance } = usePopupType();
  return (
    <h2
      data-sweepstakes-step-heading
      tabIndex={-1}
      className={cn(
        "font-ppMori text-[22px] font-semibold leading-[26px] -tracking-[0.44px] text-[#1f2022] outline-none md:text-[24px] md:leading-[28px] md:-tracking-[0.48px] xl:text-[28px] xl:leading-[34px] xl:-tracking-[0.56px] 2xl:text-[32px] 2xl:leading-[38px] 2xl:-tracking-[0.64px]",
        fullscreen &&
          "xl:text-[36px] xl:leading-[42px] xl:-tracking-[0.72px] 2xl:text-[44px] 2xl:leading-[50px] 2xl:-tracking-[0.88px] [@media(min-width:1280px)_and_(max-height:959px)]:text-[34px] [@media(min-width:1280px)_and_(max-height:959px)]:leading-[40px] [@media(min-width:1920px)_and_(min-height:1080px)]:text-[52px] [@media(min-width:1920px)_and_(min-height:1080px)]:leading-[58px] [@media(min-width:1920px)_and_(min-height:1080px)]:-tracking-[1.04px]",
        balance && "text-balance",
        className,
      )}
    >
      {children}
    </h2>
  );
};

export const DateLine: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <span
    className={cn(
      "font-ppMori text-[18px] font-medium leading-[22px] -tracking-[0.36px] text-[#1f2022] md:text-[20px] md:leading-[24px] md:-tracking-[0.4px] xl:text-[22px] xl:leading-[26px] xl:-tracking-[0.44px] 2xl:text-[24px] 2xl:leading-[28px] 2xl:-tracking-[0.48px]",
      className,
    )}
  >
    {children}
  </span>
);

export const Body: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const { fullscreen, balance } = usePopupType();
  return (
    <p
      className={cn(
        "font-ppMori text-[14px] leading-[20px] text-[#1f2022] md:text-[16px] md:leading-[22px] 2xl:text-[18px] 2xl:leading-[26px]",
        fullscreen &&
          "xl:text-[18px] xl:leading-[26px] 2xl:text-[20px] 2xl:leading-[30px] [@media(min-width:1280px)_and_(max-height:959px)]:text-[17px] [@media(min-width:1280px)_and_(max-height:959px)]:leading-[25px] [@media(min-width:1920px)_and_(min-height:1080px)]:text-[24px] [@media(min-width:1920px)_and_(min-height:1080px)]:leading-[34px]",
        balance &&
          "max-w-[440px] text-balance 2xl:max-w-[520px] [@media(min-width:1920px)_and_(min-height:1080px)]:max-w-[620px]",
        className,
      )}
    >
      {children}
    </p>
  );
};
